import type { components } from '@multaqa/api-client'
import { type InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'

export type CatalogVariant = components['schemas']['CatalogVariantResource']
export type CatalogVariantStatus = 'active' | 'inactive' | 'all'
export type CatalogProductType = components['schemas']['ProductType']

const PAGE_SIZE = 20
const SEARCH_DELAY_MS = 250

export interface CatalogVariantFilters {
  search?: string
  status?: CatalogVariantStatus
  productType?: CatalogProductType
  /** Only this supplier's products; '' (no supplier yet) lists nothing. */
  supplierId?: string
  /** Rows per page; the server's largest is 50. */
  perPage?: number
}

/** The most a supplier's list is loaded at once, to be searched in the browser. */
export const SUPPLIER_LIST_SIZE = 50

export const catalogVariantKeys = {
  all: (companyId: string | null) => ['companies', companyId, 'directory', 'variants'] as const,
  search: (companyId: string | null, filters: CatalogVariantFilters) =>
    [...catalogVariantKeys.all(companyId), 'search', filters] as const,
}

async function fetchVariantPage(
  companyId: string,
  filters: CatalogVariantFilters,
  page: number,
  perPage = PAGE_SIZE,
) {
  const response = await api.GET('/directory/variants', {
    headers: { 'X-Company-Id': companyId },
    params: {
      query: {
        search: filters.search || undefined,
        status: filters.status,
        product_type: filters.productType,
        supplier_id: filters.supplierId || undefined,
        page,
        per_page: filters.perPage ?? perPage,
      },
    },
  })
  if (response.error) throw new Error(response.error.message ?? 'Failed to load items')
  return response.data
}

/** The typed text, settled for a moment before it becomes a server search. */
export function useSettledSearch(text: string): string {
  const [settled, setSettled] = useState(text.trim())
  useEffect(() => {
    const timeout = window.setTimeout(() => setSettled(text.trim()), SEARCH_DELAY_MS)
    return () => window.clearTimeout(timeout)
  }, [text])
  return settled
}

/**
 * Pages through `GET /directory/variants`: the server matches the search on
 * part number, SKU, barcode or name, so every page is shown as it arrives.
 */
export function useCatalogVariantSearch(
  companyId: string | null,
  filters: CatalogVariantFilters,
  enabled = true,
) {
  const queryKey = catalogVariantKeys.search(companyId, filters)
  return useInfiniteQuery({
    queryKey,
    // While a new search loads, the last results stay: an empty list in the
    // gap swallowed an Enter on the highlighted row. Only for a new search
    // text; a new company, supplier, status or type shows nothing stale.
    placeholderData: (
      previous: InfiniteData<VariantPage, number> | undefined,
      previousQuery: { queryKey: readonly unknown[] } | undefined,
    ) =>
      previousQuery !== undefined && sameExceptSearch(previousQuery.queryKey, queryKey)
        ? previous
        : undefined,
    enabled: enabled && Boolean(companyId) && filters.supplierId !== '',
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchVariantPage(companyId ?? '', filters, pageParam),
    getNextPageParam: (last) =>
      last.meta.current_page < last.meta.last_page ? last.meta.current_page + 1 : undefined,
  })
}

type VariantPage = Awaited<ReturnType<typeof fetchVariantPage>>

function sameExceptSearch(previous: readonly unknown[], next: readonly unknown[]): boolean {
  const filtersOf = (key: readonly unknown[]) => ({
    ...(key[5] as CatalogVariantFilters),
    search: '',
  })
  return (
    previous[1] === next[1] &&
    JSON.stringify(filtersOf(previous)) === JSON.stringify(filtersOf(next))
  )
}

/**
 * The chosen item, when the loaded pages do not hold it (an edit, a link):
 * looked up by the code the caller knows, then matched on its id.
 */
export function useSelectedCatalogVariant(
  companyId: string | null,
  variantId: string,
  lookup: string | undefined,
  enabled: boolean,
) {
  const search = lookup?.trim() ?? ''
  return useQuery({
    queryKey: [...catalogVariantKeys.all(companyId), 'selected', variantId, search] as const,
    enabled: Boolean(companyId && variantId && search) && enabled,
    queryFn: async (): Promise<CatalogVariant | null> => {
      const page = await fetchVariantPage(companyId ?? '', { search, status: 'all' }, 1, 50)
      return page.data.find((row) => row.id === variantId) ?? null
    },
  })
}

/**
 * Whether a variant matches typed text on what the browser holds: its SKU,
 * part number or name, or its product's (a barcode needs the server).
 */
export function catalogVariantMatches(variant: CatalogVariant, text: string): boolean {
  const term = text.trim().toLocaleLowerCase()
  if (term === '') return true
  return [
    variant.sku,
    variant.part_number,
    variant.effective_part_number,
    ...Object.values(variant.name ?? {}),
    variant.product.sku,
    variant.product.part_number,
    ...Object.values(variant.product.name ?? {}),
  ].some((value) => typeof value === 'string' && value.toLocaleLowerCase().includes(term))
}
